/**
 * Days List PDF Layout
 *
 * Creates a true day-by-day itinerary layout per specification.
 * Day-by-day itinerary layout. Date, day number, activities, and places.
 * Timeline format suitable for travel reference - NO statistics or complex sections.
 */

export class DaysListPDFLayout {
  constructor() {
    this.layoutType = 'itinerary';
    this.name = 'Days List Layout';
  }

  /**
   * Generate PDF content structure for days list
   * @param {Object} pageContext - Page context with days data
   * @returns {Object} PDF content structure
   */
  async generate(pageContext) {
    const daysData = Array.isArray(pageContext.data) ? pageContext.data : [];

    if (daysData.length === 0) {
      return this.generateEmptyContent(pageContext);
    }

    // Sort days by date
    const sortedDays = this.sortDaysByDate(daysData);

    return {
      sections: [
        this.generateItinerarySection(sortedDays, pageContext)
      ].filter(section => section !== null)
    };
  }


  /**
   * Generate itinerary section - REPLICATE CURRENT PAGE FORMAT
   * Replicates exactly what's shown on the Days List page:
   * - Day number and formatted date
   * - Content from ContentBlockRenderer (day.content field)
   * @param {Array} daysData - Sorted days data (same as page uses)
   * @param {Object} pageContext - Page context
   * @returns {Object} Itinerary section
   */
  generateItinerarySection(daysData, pageContext) {
    return {
      type: 'itinerary',
      title: null, // No title - clean format matching page
      items: daysData.map((day, index) => {
        // Use the EXACT same format as DraggableDaysList component:
        // - "Day X" with index + 1
        // - Formatted date using same formatDate function
        // - day.content field (what ContentBlockRenderer displays)

        const dayNumber = `Day ${index + 1}`;
        const formattedDate = day.date ? this.formatDateForDisplay(day.date) : 'Date not set';

        // Handle different content types - day.content might be string, array, or object
        let dayContent = '';
        let hasContent = false;

        if (day.content) {
          if (typeof day.content === 'string') {
            dayContent = day.content;
            hasContent = dayContent.trim().length > 0;
          } else if (Array.isArray(day.content)) {
            // Content blocks array - extract text content
            dayContent = this.extractTextFromContentBlocks(day.content);
            hasContent = dayContent.trim().length > 0;
          } else if (typeof day.content === 'object') {
            // Object content - stringify for display
            dayContent = JSON.stringify(day.content);
            hasContent = true;
          }
        }

        return {
          dayNumber: dayNumber,
          dayDate: formattedDate,
          dayContent: dayContent,
          hasContent: hasContent,
          // Debug info to see actual day structure
          _debug: {
            dayKeys: Object.keys(day),
            contentType: typeof dayContent,
            contentLength: dayContent ? dayContent.length : 0,
            hasContent: hasContent
          }
        };
      })
    };
  }


  /**
   * Generate content for empty days list
   * @param {Object} pageContext - Page context
   * @returns {Object} Empty content structure
   */
  generateEmptyContent(pageContext) {
    return {
      sections: [{
        type: 'text',
        title: 'No Days Planned',
        content: 'This trip does not have any days planned yet. Add some days to your itinerary to generate a more detailed PDF export.'
      }]
    };
  }

  /**
   * Sort days by date
   * @param {Array} daysData - Days data
   * @returns {Array} Sorted days
   */
  sortDaysByDate(daysData) {
    return daysData
      .filter(day => day && day.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }


  /**
   * Format date for itinerary display - simple format per spec
   * @param {string} dateString - Date string
   * @returns {string} Formatted date
   */
  formatDateSimple(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format date for complete day header display
   * @param {string} dateString - Date string
   * @returns {string} Formatted date with day name
   */
  formatDateComplete(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format date for display - matches the page's formatDate function
   * @param {string} dateString - Date string
   * @returns {string} Formatted date matching page display
   */
  formatDateForDisplay(dateString) {
    try {
      const date = new Date(dateString);
      // Format to match the page's formatDate function from @trip-planner-monorepo/shared
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }



  /**
   * Extract activities for itinerary format - timeline suitable for travel reference
   * @param {Object} day - Day object
   * @returns {Array} List of formatted activities with timing
   */
  extractActivitiesForItinerary(day) {
    const activities = [];

    // Extract from content blocks with timing
    if (day.contentBlocks && Array.isArray(day.contentBlocks)) {
      day.contentBlocks.forEach(block => {
        if (block && (block.title || block.content || block.text)) {
          const activity = this.formatActivityForItinerary(block);
          if (activity) {
            activities.push(activity);
          }
        }
      });
    }

    // Extract from activities array
    if (day.activities && Array.isArray(day.activities)) {
      day.activities.forEach(activity => {
        if (typeof activity === 'string' && activity.trim()) {
          activities.push(activity);
        } else if (activity && (activity.title || activity.content)) {
          const formattedActivity = this.formatActivityForItinerary(activity);
          if (formattedActivity) {
            activities.push(formattedActivity);
          }
        }
      });
    }

    // Filter out null/empty values and ensure we have valid strings
    return activities.filter(activity => activity && typeof activity === 'string' && activity.trim());
  }

  /**
   * Extract places mentioned in the day for itinerary
   * @param {Object} day - Day object
   * @returns {Array} List of places
   */
  extractPlacesForItinerary(day) {
    const places = [];

    // Extract from content blocks
    if (day.contentBlocks && Array.isArray(day.contentBlocks)) {
      day.contentBlocks.forEach(block => {
        if (block.place) {
          places.push(block.place);
        }
      });
    }

    // Extract from places array
    if (day.places && Array.isArray(day.places)) {
      places.push(...day.places);
    }

    return places.filter(place => place);
  }

  /**
   * Extract text content from content blocks array (for ContentBlockRenderer compatibility)
   * @param {Array} contentBlocks - Array of content blocks
   * @returns {string} Extracted text content
   */
  extractTextFromContentBlocks(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return '';

    const textParts = [];

    contentBlocks.forEach(block => {
      if (!block) return;

      // Handle different content block types
      if (typeof block === 'string') {
        textParts.push(block);
      } else if (typeof block === 'object') {
        // Handle specific content block types based on how ContentBlockRenderer displays them
        if (block.type) {
          switch (block.type) {
            case 'text':
              if (block.text || block.content) {
                textParts.push(block.text || block.content);
              }
              break;

            case 'accommodation_reference':
              // Replicate how AccommodationBlock displays: "Stay at Hotel Name: City, Country"
              if (block.displayText) {
                textParts.push(block.displayText);
              } else {
                // Build display text like AccommodationBlock does
                const accommodationName = block.accommodationName || 'Unknown Accommodation';
                const location = block.placeName || '';
                const displayText = location ?
                  `Stay at ${accommodationName}: ${location}` :
                  `Stay at ${accommodationName}`;
                textParts.push(displayText);
              }
              break;

            case 'travel_reference':
              if (block.displayText) {
                textParts.push(block.displayText);
              } else if (block.title) {
                textParts.push(block.title);
              }
              break;

            case 'activity_reference':
              if (block.displayText) {
                textParts.push(block.displayText);
              } else if (block.title) {
                textParts.push(block.title);
              }
              break;

            default:
              // Generic handling - try various content fields
              const text = block.displayText || block.content || block.text || block.title || block.description || block.name;
              if (text && typeof text === 'string') {
                textParts.push(text);
              }
          }
        } else {
          // No type specified - try various content fields
          const text = block.displayText || block.content || block.text || block.title || block.description || block.name;
          if (text && typeof text === 'string') {
            textParts.push(text);
          }
        }
      }
    });

    return textParts.join('\n');
  }

  /**
   * Format activity for itinerary display
   * @param {Object} activity - Activity object
   * @returns {string} Formatted activity
   */
  formatActivityForItinerary(activity) {
    if (typeof activity === 'string') return activity.trim() || null;

    let formatted = '';

    // Add timing if available
    if (activity.time || activity.startTime) {
      formatted += `${activity.time || activity.startTime} - `;
    }

    // Add main content - check multiple possible content fields
    const mainContent = activity.title || activity.content || activity.text || activity.name || activity.description;
    if (!mainContent || mainContent.trim() === '') {
      return null; // This will be filtered out by the calling function
    }
    formatted += mainContent.trim();

    // Add location if available
    if (activity.place || activity.location) {
      formatted += ` at ${activity.place || activity.location}`;
    }

    return formatted;
  }


  /**
   * Get preview information for this layout - itinerary format per spec
   * @param {*} data - Data to analyze
   * @returns {Object} Preview information
   */
  getPreview(data) {
    const daysCount = Array.isArray(data) ? data.length : 0;

    return {
      estimatedPages: Math.max(1, Math.ceil(daysCount / 2)), // Itinerary estimate
      sections: ['Daily Itinerary'], // Only one itinerary section
      features: [
        'Day-by-day itinerary layout',
        'Date, day number, activities, and places',
        'Timeline format suitable for travel reference'
      ],
      dataCount: daysCount
    };
  }
}